import axios from 'axios';

export enum WaitlistErrorType {
    NotAvailable = 'NotAvailable',
    LimitReached = 'LimitReached',
    Invalid = 'Invalid',
    Unknown = 'Unknown'
}

export class WaitlistError extends Error {
    name = 'WaitlistError';
    type: WaitlistErrorType;

    constructor(type: WaitlistErrorType, message?: string) {
        super(message || WaitlistErrorMessages[type]);

        this.type = type;
    }
}

export const WaitlistErrorMessages: Record<WaitlistErrorType, string> = {
    [WaitlistErrorType.NotAvailable]: 'Waitlist is not available for this business',
    [WaitlistErrorType.LimitReached]:
        "You can't add another waitlist request at this time. Please contact the business for assistance.",
    [WaitlistErrorType.Invalid]: 'Oops, something went wrong.',
    [WaitlistErrorType.Unknown]: 'Oops, something went wrong.'
};

const ERROR_TYPE_BY_STATUS: Record<number, WaitlistErrorType> = {
    403: WaitlistErrorType.NotAvailable,
    429: WaitlistErrorType.LimitReached,
    422: WaitlistErrorType.Invalid
};

// Messages that don't read well after the humanized attribute ("Dates is invalid").
const MESSAGE_OVERRIDES: Record<string, Record<string, string>> = {
    'waitlist_services.dates': { 'is invalid': 'Dates are invalid' }
};

// "waitlist_services.service" -> "Service", "waitlist_services.dates" -> "Dates"
const humanizeAttribute = (attribute: string): string => {
    const lastSegment = attribute.split('.').pop() ?? attribute;
    const words = lastSegment.replace(/_id$/, '').replace(/_/g, ' ').trim();

    return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * First readable message of a Rails errors hash, e.g.
 * `{ "waitlist_services.service": ["is not available for online booking"] }`
 * -> "Service is not available for online booking".
 */
const getFirstValidationError = (data: unknown): string | undefined => {
    if (!data || typeof data !== 'object') {
        return undefined;
    }

    const errors = (data as { errors?: unknown }).errors ?? data;

    if (!errors || typeof errors !== 'object') {
        return undefined;
    }

    for (const [attribute, value] of Object.entries(errors as Record<string, unknown>)) {
        const message = Array.isArray(value) ? value.find(v => typeof v === 'string') : value;

        if (typeof message !== 'string' || message.trim().length === 0) {
            continue;
        }

        const override = MESSAGE_OVERRIDES[attribute]?.[message];

        if (override) {
            return override;
        }

        if (attribute === 'base' || /^[A-Z]/.test(message)) {
            return message;
        }

        return `${humanizeAttribute(attribute)} ${message}`;
    }

    return undefined;
};

export const convertToWaitlistError = (error: unknown): WaitlistError => {
    if (!axios.isAxiosError(error)) {
        return new WaitlistError(WaitlistErrorType.Unknown);
    }

    const status = error.response?.status ?? 0;
    const data = error.response?.data;
    const type = ERROR_TYPE_BY_STATUS[status] ?? WaitlistErrorType.Unknown;

    // 403 / 429 carry `{ success: false, message }`, shown verbatim.
    const backendMessage =
        data && typeof data === 'object' && typeof data.message === 'string'
            ? (data.message as string)
            : undefined;

    return new WaitlistError(type, backendMessage ?? getFirstValidationError(data));
};
